// connection-keeper.js
// Keeps a node connected to its bootstrap peer, and tells the truth about it.
//
// Before this, nothing noticed when the connection to the bootstrap/relay
// died: the status stayed green, nothing redialed, and the only recovery was
// clicking Connect, which also built a new node. The bootstrap connection
// matters beyond the first dial: it carries the relay reservation other
// browsers use to reach this one, and it is how new WebRTC connections get
// signalled. (Connections already open directly between two browsers don't
// depend on it.)
//
// Dead links are caught three ways: libp2p reporting the peer disconnected,
// a periodic identify round trip (catches a socket that died without any close event, e.g.
// after sleep or a dropped network), and the browser's own online /
// tab-visible events, which trigger an immediate check. Recovery redials the
// same node with exponential backoff, so peer ID, groups and open WebRTC
// links are untouched.

import { multiaddr } from '@multiformats/multiaddr'
import { peerIdFromString } from '@libp2p/peer-id'

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export class ConnectionKeeper {
  /**
   * @param {object} opts
   * @param {import('@libp2p/interface').Libp2p} opts.node
   * @param {string} opts.bootstrapAddr - the same multiaddr the node was started with
   * @param {(state: 'connecting'|'connected'|'reconnecting'|'stopped', detail?: string) => void} [opts.onState]
   * @param {number} [opts.healthIntervalMs]
   * @param {number} [opts.pingTimeoutMs]
   * @param {number} [opts.dialTimeoutMs]
   * @param {{minMs: number, maxMs: number}} [opts.backoff]
   * @param {number} [opts.maxInconclusive] - consecutive health checks that may be
   *   unable to run a ping at all before that counts as a dead link
   */
  constructor ({ node, bootstrapAddr, onState = () => {}, healthIntervalMs = 20000, pingTimeoutMs = 5000, dialTimeoutMs = 15000, backoff = { minMs: 1000, maxMs: 30000 }, maxInconclusive = 3 }) {
    this.node = node
    this.bootstrapAddr = bootstrapAddr
    this._ma = multiaddr(bootstrapAddr)
    const peerIdStr = this._ma.getComponents().findLast((c) => c.name === 'p2p')?.value
    if (!peerIdStr) throw new Error('bootstrap address needs a /p2p/<peer id> segment')
    this._peerIdStr = peerIdStr
    // libp2p's ping()/hangUp() want a PeerId (or multiaddr), not a string: a string
    // makes ping throw, which this keeper used to read as a dead connection.
    this._peerId = peerIdFromString(peerIdStr)
    this.onState = onState
    this.healthIntervalMs = healthIntervalMs
    this.pingTimeoutMs = pingTimeoutMs
    this.dialTimeoutMs = dialTimeoutMs
    this.backoff = backoff
    this.maxInconclusive = maxInconclusive
    this._inconclusive = 0
    this.state = 'stopped'
    this._stopped = true
    this._recovering = false
    this._timer = null
    this._onDisconnect = (evt) => { if (evt.detail.toString() === this._peerIdStr) this._lost('disconnected') }
    this._onWake = () => { this._healthCheck().catch(() => {}) }
  }

  start () {
    if (!this._stopped) return
    this._stopped = false
    this.node.addEventListener('peer:disconnect', this._onDisconnect)
    this._timer = setInterval(() => this._healthCheck().catch(() => {}), this.healthIntervalMs)
    globalThis.addEventListener?.('online', this._onWake)
    globalThis.document?.addEventListener?.('visibilitychange', this._onWake)
    if (this._isConnected()) {
      this._setState('connected')
    } else {
      this._setState('connecting')
      this._lost('not connected at start', 'connecting')
    }
  }

  stop () {
    this._stopped = true
    clearInterval(this._timer)
    this.node.removeEventListener('peer:disconnect', this._onDisconnect)
    globalThis.removeEventListener?.('online', this._onWake)
    globalThis.document?.removeEventListener?.('visibilitychange', this._onWake)
    this._setState('stopped')
  }

  /** Drop whatever we have to the bootstrap peer and dial it again. */
  async reconnectNow () {
    await this.node.hangUp(this._peerId).catch(() => {})
    this._lost('manual reconnect')
  }

  _isConnected () {
    return this.node.getConnections().some((c) => c.remotePeer.toString() === this._peerIdStr && c.status === 'open')
  }

  _setState (state, detail) {
    if (this.state === state) return
    this.state = state
    this.onState(state, detail)
  }

  async _healthCheck () {
    if (this._stopped || this._recovering) return
    const connection = this.node.getConnections(this._peerId).find((c) => c.status === 'open')
    if (!connection) return this._lost('no open connection')
    try {
      // identify is a real request/response with the peer, so a socket that
      // died silently times out here. It is used instead of ping because the
      // DHT keeps pinging its contacts (the bootstrap peer included) and libp2p
      // allows only one outbound ping stream per connection, so a keeper ping
      // was refused as "Too many outbound protocol streams" almost every time.
      await this.node.services.identify.identify(connection, { signal: AbortSignal.timeout(this.pingTimeoutMs) })
      this._inconclusive = 0
    } catch (err) {
      // Even so, "too many streams" means we couldn't ask, not that the peer
      // didn't answer, so it is not evidence of a dead link: skip the round.
      // Only if it persists for several rounds in a row (a stream stuck on a
      // dead socket) does it count as a failure. A probe that ran and timed
      // out or errored still does immediately.
      if (/too many outbound protocol streams/i.test(err.message) && ++this._inconclusive < this.maxInconclusive) return
      this._inconclusive = 0
      // The socket looks open but isn't answering. Close it so libp2p and the
      // relay both see a clean disconnect, then redial.
      await this.node.hangUp(this._peerId).catch(() => {})
      this._lost(`health check failed: ${err.message}`)
    }
  }

  async _lost (reason, state = 'reconnecting') {
    if (this._stopped || this._recovering) return
    this._recovering = true
    this._setState(state, reason)
    try {
      for (let attempt = 0; !this._stopped; attempt++) {
        try {
          await this.node.dial(this._ma, { signal: AbortSignal.timeout(this.dialTimeoutMs) })
          if (this._isConnected()) { this._setState('connected'); return }
        } catch { /* fall through to backoff */ }
        const delay = Math.min(this.backoff.maxMs, this.backoff.minMs * 2 ** attempt)
        await sleep(delay + Math.random() * delay * 0.25)
      }
    } finally {
      this._recovering = false
    }
  }
}
